import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Flex,
  Avatar,
  Link,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
} from "@chakra-ui/react";
import { AddIcon, BellIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import UserSearchPicker from "./UserSearchPicker";
import SchemaBadge from "./SchemaBadge";
import ChatComposer from "./ChatComposer";
import MessageAttachments from "./MessageAttachments";
import WorkItemCard from "./WorkItemCard";
import { basename } from "../env";
import { AGENT_SENDER_ID } from "../../pages/medstudent/agents/agentsService";
import { parseWorkItemHref } from "../../utils/workItemRefs";
import { sanitizeMessageHtml } from "../../utils/messageHtml";
import { seenReceipts, receiptLabel, receiptTitle } from "./readReceipts";
import { userColor } from "./userColor";
import { mentionTerms, splitMentions, highlightMentionsHtml } from "./mentions";

// A channel's own notification level (docs/specs/messaging-notifications-spec.md); "default"
// follows the person's level in the Notifications settings.
const CHANNEL_LEVELS = [
  { value: "default", label: "Default" },
  { value: "all", label: "All messages" },
  { value: "mentions", label: "Mentions & DMs" },
  { value: "none", label: "Nothing" },
];

// Mentions in a bubble: a mention of the reader in Slack's yellow, anyone else's in a light tint.
function mentionStyle(self, isOwn) {
  if (self) return { bg: "yellow.200", color: "gray.900", fontWeight: "semibold", borderRadius: "sm", px: "1px" };
  return {
    bg: isOwn ? "whiteAlpha.300" : "blue.100",
    color: isOwn ? "white" : "blue.800",
    fontWeight: "semibold",
    borderRadius: "sm",
    px: "1px",
  };
}

// The colors an agent's words may be in a post (utils/messageHtml.js), on the light bubble.
const SPAN_COLORS = {
  red: "red.600", orange: "orange.600", yellow: "yellow.700", green: "green.600",
  blue: "blue.600", purple: "purple.600", gray: "gray.600",
};

// The backend used to post a "📋 Task assigned to …" notice into the chat when
// a task was created from a message. It no longer does; hide the old ones.
const TASK_NOTICE_PREFIX = "\u{1F4CB} Task assigned to ";
function isTaskNotice(message) {
  return message.body_format !== "html" && String(message.body || "").startsWith(TASK_NOTICE_PREFIX);
}

// Files dragged in from the computer (not selected text or a link).
function isFileDrag(e) {
  return !!e.dataTransfer?.types?.includes("Files");
}

const ROBOT = String.fromCodePoint(0x1f916);
const ELLIPSIS = String.fromCharCode(0x2026);
// The server's words for a file posted in an agent's thread (backend messaging.AGENT_ATTACHMENTS;
// backend/api/tests/test_messaging_frontend_words.py keeps the two the same): a post there is the
// poster's message to the agent too, and an agent reads no file.
const AGENT_NO_FILES = "Agents can't read attachments yet. Send the text on its own.";

// What an agent's thread is called ("<robot> Morning fax brief"), without the robot: the composer
// addresses the agent by its title.
function agentTitle(name) {
  const text = String(name || "");
  return text.startsWith(ROBOT) ? text.slice(ROBOT.length).trim() : text;
}

// The agent in the Agents page. /agents is a router route, so the href carries the router's basename
// (a bare "/agents" is outside the app). It opens in a new tab, like the links inside messages do, so
// it never takes the reader away from the chart or recording they have open.
function AgentLink({ agentId, children, ...style }) {
  return (
    <Link
      href={`${basename}/agents?agent=${encodeURIComponent(agentId)}`}
      target="_blank"
      rel="noopener noreferrer"
      color="blue.500"
      fontWeight="semibold"
      flexShrink={0}
      {...style}
    >
      {children} <ExternalLinkIcon mx="2px" aria-hidden="true" />
    </Link>
  );
}

function MessageRow({ message, isOwn, receipt, onRequestAssign, onImageLoad, nameById, currentUserId }) {
  const [hovered, setHovered] = useState(false);
  const isSystem = message.sender_name === "System";
  const isHtml = message.body_format === "html";
  const tone = userColor(message.sender_name);
  const terms = useMemo(() => mentionTerms(message, nameById, currentUserId), [message, nameById, currentUserId]);
  // Highlighted after sanitizing, on the parsed text nodes (mentions.js): nothing a message or a
  // name holds can turn into markup.
  const richHtml = useMemo(
    () => (isHtml ? highlightMentionsHtml(sanitizeMessageHtml(message.body), terms) : null),
    [isHtml, message.body, terms],
  );
  return (
    <Flex
      justify={isOwn ? "flex-end" : "flex-start"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      px={2}
    >
      <HStack align="flex-start" maxW="80%" spacing={2}>
        {!isOwn && !isSystem && <Avatar size="xs" name={message.sender_name} bg={`${tone}.500`} color="white" mt={1} />}
        <Box position="relative">
          {/* The 📋 button floats beside the message instead of taking room in
              the row, so hovering never moves the bubble: on the left of your
              own (right-aligned) messages, on the right of others' (their
              avatar is on the left). Its top clears what sits above the bubble:
              others' name row, or the "via agent" line over your own. */}
          {hovered && !isSystem && (
            <IconButton
              aria-label="Assign task or order from this message"
              title="Assign task/order"
              icon={<Text fontSize="xs">📋</Text>}
              size="xs"
              variant="ghost"
              position="absolute"
              top={isOwn && !message.agent_run_id ? "4px" : "22px"}
              {...(isOwn ? { right: "100%", mr: 1 } : { left: "100%", ml: 1 })}
              onClick={() => onRequestAssign(message)}
            />
          )}
          {!isOwn && (
            <HStack spacing={1.5}>
              <Text fontSize="xs" fontWeight="bold" color={isSystem ? "gray.500" : `${tone}.600`} _dark={{ color: `${tone}.300` }}>
                {message.sender_name}
              </Text>
              {!isSystem && <SchemaBadge schema={message.sender_schema} />}
              {/* The agent's own posts aren't "via agent". Told by id, never by name. */}
              {message.agent_run_id && message.sender_id !== AGENT_SENDER_ID && (
                <Text fontSize="xs" color="purple.500">via agent</Text>
              )}
            </HStack>
          )}
          {isOwn && message.agent_run_id && (
            <Text fontSize="xs" color="purple.500" textAlign="right">via agent</Text>
          )}
          {/* A files-only message has an empty body: no empty bubble for it. */}
          {message.body ? (
            <Box
              data-testid={`message-text-${message.id}`}
              bg={isSystem ? "yellow.50" : isOwn ? "#2F5BEA" : `${tone}.50`}
              color={isOwn ? "white" : "gray.800"}
              borderRadius="lg"
              px={3}
              py={2}
              fontSize="sm"
              fontStyle={isSystem ? "italic" : "normal"}
            >
              {isHtml ? (
                <Box
                  className="chat-rich"
                  whiteSpace="pre-wrap"
                  overflowWrap="anywhere"
                  sx={{
                    p: { m: 0 },
                    "ol,ul": { pl: 5 },
                    // An agent's colored words (utils/messageHtml.js keeps these).
                    ...Object.fromEntries(Object.entries(SPAN_COLORS).map(([name, shade]) => [
                      `span[data-color="${name}"]`,
                      { color: isOwn ? `${name}.100` : shade, fontWeight: "semibold" },
                    ])),
                    a: { textDecoration: "underline", color: isOwn ? "white" : "blue.600" },
                    code: { bg: isOwn ? "blue.600" : "gray.200", px: 1, borderRadius: "sm", fontFamily: "mono" },
                    'span[data-mention="other"]': mentionStyle(false, isOwn),
                    'span[data-mention="self"]': mentionStyle(true, isOwn),
                    'a[href^="#task-"], a[href^="#order-"]': {
                      textDecoration: "none",
                      display: "inline-block",
                      bg: isOwn ? "whiteAlpha.300" : "purple.50",
                      color: isOwn ? "white" : "purple.700",
                      border: "1px solid",
                      borderColor: isOwn ? "whiteAlpha.500" : "purple.200",
                      borderRadius: "md",
                      px: 1.5,
                      fontWeight: "semibold",
                      cursor: "pointer",
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: richHtml }}
                />
              ) : (
                <Box whiteSpace="pre-wrap">
                  {terms.length
                    ? splitMentions(message.body, terms).map((part, i) =>
                        part.mention ? (
                          <Box as="span" key={i} data-mention={part.mention.self ? "self" : "other"} {...mentionStyle(part.mention.self, isOwn)}>
                            {part.text}
                          </Box>
                        ) : (
                          <React.Fragment key={i}>{part.text}</React.Fragment>
                        ),
                      )
                    : message.body}
                </Box>
              )}
            </Box>
          ) : null}
          <MessageAttachments
            channelId={message.channel_id}
            attachments={message.attachments}
            isOwn={isOwn}
            onImageLoad={onImageLoad}
          />
          {receipt && (
            <Text
              data-testid={`read-receipt-${message.id}`}
              fontSize="xs"
              color={receipt.seen ? "blue.600" : "gray.500"}
              textAlign={isOwn ? "right" : "left"}
              mt={0.5}
              title={receiptTitle(receipt)}
            >
              {receiptLabel(receipt)}
            </Text>
          )}
        </Box>
      </HStack>
    </Flex>
  );
}

// The bell in the header: what this channel notifies about, and mute.
function ChannelNotifyMenu({ settings, onChange }) {
  const level = settings?.notify_level || "default";
  const muted = !!settings?.muted;
  return (
    <Menu isLazy placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label="Channel notifications"
        title={muted ? "Muted" : "Notifications"}
        icon={<BellIcon />}
        size="xs"
        variant="ghost"
        color={muted ? "gray.400" : undefined}
        flexShrink={0}
      />
      <MenuList fontSize="sm" zIndex={3}>
        <MenuOptionGroup
          title="Notify me about"
          type="radio"
          value={level}
          onChange={(value) => value !== level && onChange({ notify_level: value })}
        >
          {CHANNEL_LEVELS.map((l) => (
            <MenuItemOption key={l.value} value={l.value}>
              {l.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
        <MenuDivider />
        <MenuItem onClick={() => onChange({ muted: !muted })}>{muted ? "Unmute" : "Mute"}</MenuItem>
      </MenuList>
    </Menu>
  );
}

// `notice`, optional: a line shown under the header (MessagingModal: the live feed has stopped).
// `notifySettings` ({notify_level, muted}) and `onChangeNotify(patch)`, optional: the channel's
// notification menu. readBy (every other member) also feeds the composer's @list and the names
// of the people a message mentions.
export default function ChannelThread({ channel, messages, readBy = [], currentUser, schema, linkableItems, onSend, onRequestAssign, onInvite, notice = null, notifySettings = null, onChangeNotify = null }) {
  const [showInvitePicker, setShowInvitePicker] = useState(false);
  const [openCard, setOpenCard] = useState(null); // { kind, id, schema, rect } of a clicked 📋 chip
  const listHostRef = useRef(null);
  const bottomRef = useRef(null);
  const composerRef = useRef(null);
  // Whether the thread is following its newest message: true after every
  // jump to the bottom and while the reader stays there; false once they
  // scroll up to read history.
  const followingRef = useRef(true);
  const lastScrollTopRef = useRef(0);

  // Everyone in the conversation by id (the other members, from readBy, and me): the names a
  // message's mentions are shown with, and who the composer's @list offers. NotesAI Agent is not
  // someone to mention.
  const nameById = useMemo(() => {
    const names = {};
    (readBy || []).forEach((r) => {
      if (r?.user_id && r.name) names[r.user_id] = r.name;
    });
    if (currentUser?.id && currentUser.name) names[currentUser.id] = currentUser.name;
    return names;
  }, [readBy, currentUser?.id, currentUser?.name]);
  const mentionMembers = useMemo(
    () => (readBy || []).filter((r) => r?.user_id && r.user_id !== AGENT_SENDER_ID && r.name && r.name !== "Unknown"),
    [readBy],
  );

  useEffect(() => {
    followingRef.current = true;
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages]);

  // A receipt line appearing under the newest message would sit just below
  // the fold; keep it in view unless the reader has scrolled up.
  const shownReadByRef = useRef(readBy);
  useEffect(() => {
    if (shownReadByRef.current === readBy) return;
    shownReadByRef.current = readBy;
    if (followingRef.current) bottomRef.current?.scrollIntoView?.({ block: "end" });
  }, [readBy]);

  // MessagingModal renders one ChannelThread for every channel (it has no key), so an open invite
  // picker would follow the reader into the next conversation, a DM or a public channel included.
  // It belongs to one channel: close it when the channel changes.
  useEffect(() => {
    setShowInvitePicker(false);
  }, [channel?.id]);
  // A 📋 chip in a message opens that task's or order's card over the thread.
  const handleListClick = (e) => {
    const link = e.target?.closest?.("a[href]");
    const ref = link ? parseWorkItemHref(link.getAttribute("href")) : null;
    if (!ref) return;
    e.preventDefault();
    const host = listHostRef.current?.getBoundingClientRect();
    const box = link.getBoundingClientRect();
    setOpenCard({
      ...ref,
      rect: { left: box.left - (host?.left || 0), top: box.top - (host?.top || 0), bottom: box.bottom - (host?.top || 0) },
    });
  };
  // The card stays put when a new message scrolls the list (a half-typed
  // comment would be lost), but closes when the reader scrolls away from it.
  const closeCard = () => setOpenCard(null);

  const handleListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 48) followingRef.current = true;
    else if (el.scrollTop < lastScrollTopRef.current) followingRef.current = false;
    lastScrollTopRef.current = el.scrollTop;
  };

  // A thumbnail has no height until its image loads, so the jump to the
  // newest message lands short of a freshly sent picture. Once it loads, go
  // to the bottom again, unless the reader has scrolled up meanwhile.
  const handleImageLoad = useCallback(() => {
    if (followingRef.current) bottomRef.current?.scrollIntoView?.({ block: "end" });
  }, []);

  // An agent's own thread (Plan 4 Task 9): every agent has one, "<robot> <title>", told by its
  // agent_id. Its members write here as in any channel, and it is the same conversation as the
  // agent's chat in the Agents tab: a post is the poster's message to the agent too. Its members are
  // the agent's, managed in the agent.
  const agentId = channel?.agent_id || null;
  // The NotesAI Agent DM that agents posted to before they had threads of their own: kept as
  // history, read-only. Told by id, never by name: a person can be called "NotesAI Agent" too.
  const retiredAgentDm = !agentId && channel?.other_user_id === AGENT_SENDER_ID;

  if (!channel) {
    return (
      <Flex flex="1" align="center" justify="center" color="gray.400">
        Select a channel to start messaging
      </Flex>
    );
  }

  const shown = messages.filter((m) => !isTaskNotice(m));
  const receipts = seenReceipts(shown, currentUser.id, readBy, { perMessage: channel.channel_type !== "dm" });

  // Files dropped anywhere on the conversation join the draft, exactly as if
  // dropped on the composer (whose own drop handler takes drops on itself).
  return (
    <Flex
      direction="column"
      flex="1"
      h="100%"
      onDragOver={(e) => {
        if (isFileDrag(e)) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        composerRef.current?.addFiles(e.dataTransfer.files);
      }}
    >
      <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.200">
        <HStack justify="space-between">
          <Box minW={0}>
            <HStack spacing={2}>
              <Text fontWeight="bold">
                {channel.channel_type === "dm"
                  ? channel.dm_label || "Direct message"
                  : agentId
                    ? channel.name
                    : `# ${channel.name}`}
              </Text>
              {agentId && (
                <Badge colorScheme="purple" flexShrink={0}>
                  Agent
                </Badge>
              )}
              {channel.channel_type === "dm" && <SchemaBadge schema={channel.dm_schema} />}
            </HStack>
            {channel.channel_type === "dm" && channel.dm_email && channel.dm_email !== channel.dm_label && (
              <Text fontSize="sm" color="gray.500">
                {channel.dm_email}
              </Text>
            )}
          </Box>
          <HStack spacing={2} flexShrink={0}>
            {onChangeNotify && !retiredAgentDm && (
              <ChannelNotifyMenu settings={notifySettings} onChange={onChangeNotify} />
            )}
            {agentId ? (
              <AgentLink agentId={agentId} fontSize="sm">
                Open in Agents
              </AgentLink>
            ) : (
              channel.channel_type === "private" && (
                <IconButton
                  aria-label="Invite someone to this channel"
                  title="Invite"
                  icon={<AddIcon />}
                  size="xs"
                  onClick={() => setShowInvitePicker((v) => !v)}
                />
              )
            )}
          </HStack>
        </HStack>
        {channel.description && (
          <Text fontSize="sm" color="gray.500">
            {channel.description}
          </Text>
        )}
        {agentId && (
          <HStack justify="space-between" align="baseline" spacing={3} mt={1}>
            <Text fontSize="xs" color="gray.500">
              Same conversation as this agent's chat in the Agents tab.
            </Text>
            {/* In place of the invite button: the thread's members are the agent's. */}
            <AgentLink agentId={agentId} fontSize="xs">
              Members are managed in the agent
            </AgentLink>
          </HStack>
        )}
        {showInvitePicker && !agentId && (
          <UserSearchPicker
            schema={schema}
            placeholder="Invite someone by name..."
            autoFocus
            onCancel={() => setShowInvitePicker(false)}
            onPick={(user) => {
              setShowInvitePicker(false);
              onInvite(user);
            }}
          />
        )}
      </Box>
      {notice}

      <Box position="relative" flex="1" minH={0} display="flex" flexDirection="column" ref={listHostRef}>
        <VStack
          align="stretch"
          flex="1"
          overflowY="auto"
          spacing={2}
          py={3}
          data-testid="message-list"
          onScroll={handleListScroll}
          onClick={handleListClick}
          onWheel={closeCard}
          onTouchMove={closeCard}
        >
          {shown.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              isOwn={m.sender_id === currentUser.id}
              receipt={receipts[m.id]}
              onRequestAssign={onRequestAssign}
              onImageLoad={handleImageLoad}
              nameById={nameById}
              currentUserId={currentUser.id}
            />
          ))}
          <div ref={bottomRef} />
        </VStack>
        {openCard && (
          <WorkItemCard
            key={`${openCard.kind}:${openCard.id}`}
            kind={openCard.kind}
            id={openCard.id}
            schema={openCard.schema}
            currentUserId={currentUser.id}
            currentUserSchema={schema}
            anchorRect={openCard.rect}
            hostWidth={listHostRef.current?.clientWidth || 0}
            hostHeight={listHostRef.current?.clientHeight || 0}
            onClose={() => setOpenCard(null)}
          />
        )}
      </Box>

      {/* Keyed by channel: a draft (and its queued files) belongs to one
          conversation and must never follow the user into another. The ref
          follows whichever composer is showing. In an agent's thread it is
          addressed to the agent and takes no files (the server refuses them). */}
      {retiredAgentDm ? (
        <Box p={3} borderTopWidth="1px" fontSize="sm" color="gray.600">
          Agents now have their own threads under Agents.
        </Box>
      ) : (
        <ChatComposer
          key={channel.id}
          ref={composerRef}
          linkableItems={linkableItems}
          placeholder={agentId ? `Message ${agentTitle(channel.name)}${ELLIPSIS}` : undefined}
          noFiles={agentId ? AGENT_NO_FILES : null}
          note={agentId ? "Also shows in the Agents tab." : null}
          // An agent's thread is addressed to the agent: no @list there.
          mentionMembers={agentId ? null : mentionMembers}
          isDm={channel.channel_type === "dm"}
          onSend={(...args) => onSend(...args)}
        />
      )}
    </Flex>
  );
}
